import React, { useEffect, useRef, useState } from "react";
import Brand from "./components/Brand";
import Contact from "./components/Contact";
import LegalPage from "./components/LegalPage";
import SiteFooter from "./components/SiteFooter";
import "./css/styles.css";

type IconName =
  | "archive"
  | "arrow"
  | "building"
  | "camera"
  | "check"
  | "clock"
  | "document"
  | "doubleCheck"
  | "inbox"
  | "integration"
  | "mail"
  | "plate"
  | "scan"
  | "shield"
  | "spark"
  | "user";

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, React.ReactNode> = {
    archive: <><path d="M3 6h18v4H3z"/><path d="M5 10v10h14V10"/><path d="M10 14h4"/></>,
    arrow: <><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></>,
    building: <><path d="M4 21V8l8-4 8 4v13"/><path d="M3 21h18"/><path d="M10 21v-6h4v6"/></>,
    camera: <><path d="M14.5 5 13 3H9L7.5 5H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z"/><circle cx="11" cy="12" r="4"/></>,
    check: <path d="m5 12 4 4L19 6"/>,
    clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
    document: <><path d="M6 3h8l4 4v14H6z"/><path d="M14 3v5h5M9 13h6M9 17h6"/></>,
    doubleCheck: <><path d="m2 12 4 4 8-8"/><path d="m11 16 2 2 9-9"/></>,
    inbox: <><path d="M3 13h5l1.5 3h5l1.5-3h5"/><path d="M5 5h14l2 8v6H3v-6z"/></>,
    integration: <><circle cx="6" cy="6" r="3"/><circle cx="18" cy="6" r="3"/><circle cx="12" cy="18" r="3"/><path d="m8.5 7.5 2.2 7.5m4.8-7.5L13.3 15M9 6h6"/></>,
    mail: <><path d="M3 6h18v12H3z"/><path d="m3 7 9 6 9-6"/></>,
    plate: <><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M6 6v12"/><path d="M10 10h9M10 14h6"/></>,
    scan: <><path d="M4 8V5h3M20 8V5h-3M4 16v3h3M20 16v3h-3"/><path d="M7 12h10"/></>,
    shield: <><path d="M12 2 4 5v6c0 5 3.4 8.7 8 11 4.6-2.3 8-6 8-11V5z"/><path d="m8.5 12 2.3 2.3 4.7-5"/></>,
    spark: <><path d="m12 2 1.7 5.3L19 9l-5.3 1.7L12 16l-1.7-5.3L5 9l5.3-1.7z"/><path d="m19 15 .8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z"/></>,
    user: <><circle cx="12" cy="8" r="3.5"/><path d="M5 20a7 7 0 0 1 14 0"/></>,
  };

  return (
    <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

// Kept in sync by hand with the FAQPage JSON-LD in public/index.html. Changing a
// question or answer here without changing it there makes the structured data
// contradict the page.
const faqs = [
  {
    question: "Wie bearbeitet Gatai einen Bußgeldbescheid für ein Mietfahrzeug?",
    answer: "Sie fotografieren den Bescheid mit dem Handy ab – auch mehrseitig. Gatai liest Kennzeichen, Aktenzeichen, Tatzeitpunkt, Behörde und Beträge aus, sucht die passende Buchung und erstellt daraus eine fertige E-Mail an den verantwortlichen Mieter. Sie prüfen den Entwurf und geben ihn frei.",
  },
  {
    question: "Woher weiß Gatai, wer gefahren ist?",
    answer: "Aus Ihren eigenen Buchungsdaten. Kennzeichen und Tatzeitpunkt aus dem Bescheid werden mit dem Mietzeitraum abgeglichen, der zu diesem Zeitpunkt lief. Ist die Zuordnung eindeutig, entsteht der Entwurf automatisch. Ist sie es nicht – zwei mögliche Mieter, kein passender Mietzeitraum – landet der Vorgang unter „Aktion nötig“ und Sie entscheiden.",
  },
  {
    question: "Brauchen wir dafür eine bestimmte Vermietsoftware?",
    answer: "Nein. Gatai setzt kein bestimmtes Vermietungs-Verwaltungssystem voraus und ersetzt auch keines. Ihre Buchungsdaten kommen aus dem System, das Sie ohnehin nutzen – kein Systemwechsel und kein IT-Projekt auf Ihrer Seite.",
  },
  {
    question: "Was, wenn ein Kennzeichen falsch erkannt wird?",
    answer: "Weicht ein gelesenes Kennzeichen nur in einem Zeichen von einem Ihrer Fahrzeuge ab, erkennt Gatai das und korrigiert es. Die Korrektur wird Ihnen aber immer angezeigt, bevor Sie senden – im Entwurf steht dann sichtbar, was ursprünglich gelesen wurde und was daraus geworden ist. Sie können sie jederzeit von Hand überschreiben.",
  },
  {
    question: "Verschickt Gatai E-Mails automatisch an unsere Mieter?",
    answer: "Nein. Jede Nachricht landet zuerst als Entwurf in der Ausgangsrampe. Betreff und Text sind vor dem Versand frei bearbeitbar, und nichts verlässt Ihr Unternehmen ohne ausdrückliche Freigabe. So bleibt die Verantwortung für die Fahrerbenennung bei Ihnen.",
  },
  {
    question: "Welche Schreiben lassen sich scannen?",
    answer: "Bußgeldbescheide, Anhörungsbögen, Zeugenfragebögen und Verwarnungsgelder – ein- oder mehrseitig. Jede Seite wird beim Erfassen automatisch freigestellt, gerade gezogen und für die Lesbarkeit aufbereitet.",
  },
  {
    question: "Warum lohnt sich das für eine Autovermietung?",
    answer: "Halterschreiben sind fristgebunden. Wer den Fahrer nicht rechtzeitig benennt, riskiert die Kostentragung nach § 25a StVG oder eine Fahrtenbuchauflage nach § 31a StVZO. Gatai verkürzt die manuelle Recherche in den Buchungsdaten auf wenige Sekunden.",
  },
  {
    question: "Was passiert mit den Daten unserer Mieter?",
    answer: "Bescheide und Mieterdaten werden ausschließlich auf Servern in Deutschland verarbeitet und sind streng nach Vermietung getrennt. Zugriff hat nur, wer für Ihr Unternehmen angemeldet ist. Details stehen in der Datenschutzerklärung.",
  },
];

function Navigation() {
  return (
    <header className="site-header">
      <div className="nav-shell">
        <Brand href="#top" productLabel="Bußgeldmanagement" />
        <nav className="desktop-nav" aria-label="Hauptnavigation">
          <a href="#produkt">Produkt</a>
          <a href="#datenschutz">Datenschutz</a>
          <a href="#integration">Integration</a>
          <a href="#faq">FAQ</a>
        </nav>
        <div className="nav-actions">
          <a className="nav-cta" href="#kontakt">Jetzt testen</a>
          <a className="nav-login" href="/vermietungen/login">
            <svg className="nav-login-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="4.5" y="10.5" width="15" height="10" rx="2.6"/>
              <path d="M8 10.5V7.6a4 4 0 0 1 8 0v2.9"/>
              <circle cx="12" cy="15.5" r="1.3"/>
            </svg>
            Login
          </a>
        </div>
      </div>
    </header>
  );
}

/** How long each slide holds before the next one takes over. */
const SLIDE_MS = 8000;

const PREVIEW_STEPS = [
  {
    label: "Scannen",
    title: "Bescheid scannen.",
    lead: "Scannen Sie Bußgeldbescheide mit Ihrem eigenen Handy.",
    note: "Seiten werden automatisch freigestellt.",
  },
  {
    label: "Erkennen",
    title: "Das haben wir erkannt.",
    lead: "Gatai strukturiert die Daten aus Ihrem Bußgeldbescheid.",
    note: "Angaben aus dem Bescheid, automatisch ausgelesen.",
  },
  {
    label: "Mieter zuordnen",
    title: "Mieter zugeordnet.",
    lead: "Der Bußgeldbescheid wird einem Mieter in Ihrem Vermietungs-Verwaltungssystem zugeordnet.",
    note: "Zuordnung steht fest.",
  },
  {
    label: "Prüfen & senden",
    title: "Entwurf bereit.",
    lead: "Ein personalisierter E-Mail-Entwurf wird generiert.",
    note: "Nichts geht raus ohne Ihre Freigabe.",
  },
];

function PreviewStage({ step }: { step: number }) {
  if (step === 0) {
    return (
      <div className="scan-pages" aria-hidden="true">
        <div className="scan-page"><Icon name="document"/><strong>Seite 1</strong><small>erfasst</small></div>
        <div className="scan-page"><Icon name="document"/><strong>Seite 2</strong><small>erfasst</small></div>
        <div className="scan-page scanning"><Icon name="camera"/><strong>Seite 3</strong><small>wird erfasst …</small></div>
      </div>
    );
  }

  if (step === 1) {
    return (
      <div className="service-grid">
        {[
          ["plate", "B-MW 2481"],
          ["document", "Az. 1174.2/26"],
          ["clock", "12.06., 14:32"],
          ["shield", "Bußgeldstelle Berlin"],
          ["archive", "Parkverstoß"],
          ["user", "Halter: Alpenmobil"],
        ].map(([icon, label]) => (
          <div className="service-choice" key={label}>
            <span><Icon name={icon as IconName}/></span>{label}
          </div>
        ))}
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="match-card" aria-hidden="true">
        <div className="match-side">
          <small>Bußgeldbescheid</small>
          <strong>B-MW 2481</strong>
          <span>12.06., 14:32</span>
        </div>
        <div className="match-link"><Icon name="arrow"/></div>
        <div className="match-side matched">
          <small>Mietvorgang</small>
          <strong>L. Brandt</strong>
          <span>12.06., 09:00–18:00</span>
          <i><Icon name="check"/></i>
        </div>
      </div>
    );
  }

  return (
    <div className="draft-preview" aria-hidden="true">
      <div className="draft-row"><span>An</span><strong>l.brandt@example.de</strong></div>
      <div className="draft-row"><span>Betreff</span><strong>Verkehrsverstoß während Ihrer Mietzeit – B-MW 2481</strong></div>
      <p className="draft-body">Guten Tag Herr Brandt, uns wurde von der zuständigen Behörde ein Verkehrsverstoß gemeldet, der während Ihrer Mietzeit festgestellt wurde …</p>
      <div className="draft-foot">
        <span className="draft-chip"><Icon name="document"/> Bussgeldbescheid_B-MW-2481.pdf</span>
      </div>
    </div>
  );
}

function ProductPreview() {
  const [active, setActive] = useState(0);
  // Any deliberate interaction hands control to the reader; nothing takes it
  // back, because a slider that resumes on its own moves the thing someone is
  // reading.
  const [frozen, setFrozen] = useState(false);
  const railRef = useRef<HTMLElement>(null);

  const goTo = (index: number) => {
    setFrozen(true);
    setActive(index);
  };

  // Below 820px the step rail is a horizontal strip, so an advancing slider
  // would leave the active step off to the right. Guarded on scrollWidth, which
  // only exceeds clientWidth in that layout - the desktop column never scrolls.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || rail.scrollWidth <= rail.clientWidth) return;
    const step = rail.querySelectorAll<HTMLElement>(".preview-step")[active];
    if (step) rail.scrollTo({ left: Math.max(step.offsetLeft - 16, 0), behavior: "smooth" });
  }, [active]);

  useEffect(() => {
    if (frozen) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setTimeout(
      () => setActive((current) => (current + 1) % PREVIEW_STEPS.length),
      SLIDE_MS
    );
    return () => window.clearTimeout(timer);
  }, [active, frozen]);

  const step = PREVIEW_STEPS[active];

  return (
    <div className="product-preview" aria-label="Beispielansicht der Gatai App: vom Scan zum fertigen Entwurf">
      <div className="preview-topbar">
        <div className="window-dots" aria-hidden="true"><span/><span/><span/></div>
        <div className="preview-brand"><span className="mini-mark"><Icon name="spark" /></span> Alpenmobil Autovermietung</div>
        <span className="secure-label"><Icon name="shield" /> Sicher übertragen</span>
      </div>
      <div className="preview-body">
        <aside className="preview-steps" aria-label="Fortschritt" ref={railRef}>
          <p className="eyebrow">Ihr Vorgang</p>
          {PREVIEW_STEPS.map((item, index) => (
            <button
              type="button"
              className={`preview-step ${index === active ? "active" : ""}`}
              key={item.label}
              aria-current={index === active}
              onClick={() => goTo(index)}
            >
              <span>{String(index + 1).padStart(2, "0")}</span>{item.label}
            </button>
          ))}
          {/* Soft hyphen at the compound boundary. `hyphens: auto` needs a German
              hyphenation dictionary the browser may not carry, and without a
              break hint this word is wider than the rail. The emoji is joined to
              it by a non-breaking space so it never lands on a line of its own. */}
          <div className="preview-help">
            In wenigen Sekunden vom Dokumenten-Scan zur Bescheid&shy;übermittlung&nbsp;
            <span className="preview-help-emoji" aria-hidden="true">😎</span>
          </div>
        </aside>
        <div className="preview-content">
          <div className="preview-progress" role="group" aria-label="Schritt wählen">
            {PREVIEW_STEPS.map((item, index) => (
              <button
                type="button"
                key={item.label}
                className={index < active ? "done" : ""}
                aria-label={`Schritt ${index + 1}: ${item.label}`}
                aria-current={index === active}
                onClick={() => goTo(index)}
              >
                {index === active && (
                  // Remounted on every change so the fill animation restarts.
                  <i key={`${active}-${frozen}`} className={frozen ? "held" : "running"} />
                )}
              </button>
            ))}
          </div>
          <p className="preview-kicker">Schritt {active + 1} von 4</p>
          <h2>{step.title}</h2>
          <p>{step.lead}</p>
          <div className="preview-stage" key={active}>
            <PreviewStage step={active} />
          </div>
          <div className="preview-actions">
            <span>{step.note}</span>
            {/* Wraps from the last step back to the first, the same way the
                rotation does - the mock is a loop, not a wizard that ends. */}
            <button type="button" onClick={() => goTo((active + 1) % PREVIEW_STEPS.length)}>
              Weiter <Icon name="arrow"/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/impressum") return <LegalPage page="impressum" />;
  if (path.toLowerCase() === "/datenschutz") return <LegalPage page="datenschutz" />;
  // The Konfigurator's "Rechtliches" tab links here, and everything under
  // /vermietungen except /vermietungen/konfigurator and /vermietungen/login
  // falls through gatai-session-router to this pool.
  if (path.toLowerCase() === "/vermietungen/avv") return <LegalPage page="avv" />;
  if (path.toLowerCase() === "/vermietungen/agb") return <LegalPage page="agb" />;

  return (
    <div className="app" id="top">
      <Navigation />
      <main>
        <section className="hero section-shell" aria-labelledby="hero-title">
          <div className="hero-glow hero-glow-one" aria-hidden="true" />
          <div className="hero-glow hero-glow-two" aria-hidden="true" />
          <p className="beta-badge"><span/> Jetzt in der Beta-Phase</p>
          <h1 id="hero-title">Bußgeldbescheide für Mietfahrzeuge.<br/><span>Scannen, zuordnen, weiterleiten.</span></h1>
          {/* Deliberately short: the slider directly below walks through the same
              four steps in detail, so spelling them out here read as the same
              thing twice. */}
          <p className="hero-copy">
            Das Bußgeldmanagement für Autovermietungen: Bescheid abfotografieren – Gatai liest ihn aus, ordnet den Mieter zu und legt die fertige E-Mail zur Freigabe bereit.
          </p>
          <div className="hero-actions">
            <a className="button button-dark" href="#kontakt">Jetzt Demo vereinbaren <Icon name="arrow"/></a>
            <a className="button button-light" href="#produkt">Produkt ansehen</a>
          </div>
          <div className="hero-trust" aria-label="Produktvorteile">
            <span><Icon name="check"/> Fertiger Entwurf ohne Abtippen</span>
            <span><Icon name="check"/> Kein Versand ohne Ihre Freigabe</span>
            <span><Icon name="check"/> Server in Deutschland</span>
          </div>
          <ProductPreview />
        </section>

        <section className="proof-strip" aria-label="Zielgruppen">
          <div className="section-shell proof-inner">
            <p>Gebaut für die Halterpost von</p>
            <span>Autovermietungen</span><i/> <span>Wohnmobilvermietung</span><i/> <span>Transportervermietung</span><i/> <span>Carsharing</span><i/> <span>Fuhrparks</span>
          </div>
        </section>

        <section className="section section-shell" id="produkt" aria-labelledby="features-title">
          <div className="section-heading centered">
            <p className="eyebrow">Alles an einem Ort</p>
            <h2 id="features-title">Alles, was die Bearbeitung<br/>eines Bescheids braucht.</h2>
            <p>Weniger Suchen in den Buchungen, weniger Tippen, kein Vorgang, der liegen bleibt – von der Briefpost bis zur nachweislich zugestellten E-Mail an den Mieter.</p>
          </div>
          <div className="bento-grid">
            <article className="feature-card feature-large">
              <div className="feature-icon"><Icon name="scan"/></div>
              <h3>Bescheid abfotografieren, Daten erscheinen</h3>
              <p>Die Kamera zieht jede Seite gerade und hebt den Text an – auch bei mehrseitigen Schreiben. Aus dem Scan liest Gatai die Angaben aus, auf die es ankommt.</p>
              <div className="question-demo" aria-hidden="true">
                <div className="demo-question"><span>1</span><div><strong>Bußgeldbescheid · 2 Seiten</strong><small>Automatisch freigestellt und gerade gezogen.</small></div></div>
                <div className="demo-options"><span>Kennzeichen</span><span>Tatzeitpunkt</span><span>Aktenzeichen</span><span>Behörde</span></div>
              </div>
            </article>
            <article className="feature-card">
              <div className="feature-icon coral"><Icon name="user"/></div>
              <h3>Mieter automatisch zugeordnet</h3>
              <p>Kennzeichen und Tatzeitpunkt treffen auf Ihre Buchungen – der verantwortliche Mieter steht fest, ohne manuelle Recherche.</p>
              <div className="safety-status"><span><Icon name="check"/></span><div><strong>Buchung 20482</strong><small>L. Brandt · 12.06., 09:00–18:00</small></div></div>
            </article>
            <article className="feature-card">
              <div className="feature-icon mint"><Icon name="mail"/></div>
              <h3>Fertige E-Mail statt leerem Blatt</h3>
              <p>Die Fahrerbenennung entsteht aus Ihrer eigenen Vorlage – mit Anschreiben und dem Bescheid als PDF im Anhang.</p>
              <div className="upload-demo" aria-hidden="true">
                <div><Icon name="mail"/><strong>Weiterleitung an Mieter</strong><small>Bescheid als PDF angehängt</small></div>
                <span>Entwurf bereit</span>
              </div>
            </article>
            <article className="feature-card">
              <div className="feature-icon amber"><Icon name="inbox"/></div>
              <h3>Aktion nötig statt stiller Fehler</h3>
              <p>Unlesbares Kennzeichen, zwei mögliche Mieter, fehlende Adresse: Jeder unklare Fall wird Ihnen vorgelegt – und ist mit einem Klick behoben.</p>
              <div className="safety-status"><span className="warn"><Icon name="user"/></span><div><strong>Mehrere Mieter möglich</strong><small>Wer hatte das Fahrzeug zur Tatzeit?</small></div></div>
            </article>
            <article className="feature-card">
              <div className="feature-icon sky"><Icon name="doubleCheck"/></div>
              <h3>Sie sehen, ob die Nachricht ankam</h3>
              <p>Versendet, geöffnet oder nicht zugestellt – und wenn etwas schiefging, steht der Grund im Klartext daneben.</p>
              <div className="delivery-demo" aria-hidden="true">
                <span className="ok"><Icon name="doubleCheck"/> Geöffnet</span>
                <span className="bad">E-Mail existiert nicht</span>
              </div>
            </article>
            <article className="feature-card feature-wide">
              <div>
                <div className="feature-icon violet"><Icon name="integration"/></div>
                <h3>Unabhängig von Ihrem Verwaltungssystem</h3>
                <p>Gatai setzt kein bestimmtes Verwaltungssystem voraus und ersetzt auch keines. Ihre Buchungsdaten kommen aus dem System, das Sie ohnehin nutzen – kein Systemwechsel, kein IT-Projekt.</p>
              </div>
              <div className="integration-mini" aria-hidden="true">
                <span>Bescheid</span><Icon name="arrow"/><strong>Gatai</strong><Icon name="arrow"/><span>Mieter</span>
              </div>
            </article>
          </div>
        </section>

        <section className="section section-shell" id="datenschutz" aria-labelledby="privacy-title">
          <div className="privacy-panel">
            <div className="privacy-copy">
              <p className="eyebrow">DSGVO-konform</p>
              <h2 id="privacy-title">Ausgezeichnete<br/>Datensicherheit.</h2>
              <p>Der Datenschutz und die Datensicherheit sind von Beginn an ein fester Bestandteil von Gatai.</p>
              <ul className="privacy-list">
                <li><Icon name="check"/> Hosting, Datenbank und Messaging in Deutschland</li>
                <li><Icon name="check"/> Auftragsverarbeitungsvertrag nach Art. 28 DSGVO</li>
                <li><Icon name="check"/> Jede Vermietung sieht ausschließlich ihre eigenen Daten</li>
              </ul>
              <a className="button button-light privacy-cta" href="/Datenschutz">Datenschutz bei Gatai <Icon name="arrow"/></a>
            </div>
            <div className="privacy-art" aria-hidden="true">
              <svg viewBox="0 0 200 220" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M100 8 24 34v76c0 51 33 88 76 104 43-16 76-53 76-104V34z" fill="#e8f8f3" stroke="#1ca878" strokeWidth="5" strokeLinejoin="round"/>
                <path d="M100 24 38 45v65c0 43 27 74 62 88 35-14 62-45 62-88V45z" fill="#1ca878" opacity="0.14"/>
                <path d="m70 112 22 22 44-48" stroke="#12795f" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </section>

        <section className="section section-shell" id="integration" aria-labelledby="integration-title">
          <div className="integration-panel">
            <div className="integration-copy">
              <p className="eyebrow light">Ohne Umbau</p>
              <h2 id="integration-title">Startet mit der Handykamera.<br/>Fügt sich in Ihren Ablauf ein.</h2>
              <p>Sie behalten Ihre Abläufe und Ihre Systeme. Gatai übernimmt genau den Teil dazwischen: vom Brief im Posteingang bis zur nachweislich zugestellten E-Mail an den Mieter. Mehr als ein Handy und einen Browser braucht Ihr Team dafür nicht.</p>
              <ul>
                <li><Icon name="check"/> Mobile App für iOS und Android</li>
                <li><Icon name="check"/> Web-Zugang für Ihr Büro</li>
                <li><Icon name="check"/> Ihre Buchungsdaten aus dem System, das Sie schon nutzen</li>
              </ul>
            </div>
            <div className="architecture" aria-label="Ablauf im Überblick">
              {[
                ["01", "Posteingang", "Brief · Handykamera · Mehrseitig"],
                ["02", "Erkennung", "Kennzeichen · Tatzeit · Aktenzeichen"],
                ["03", "Zuordnung", "Buchung · Mieter · Kontaktdaten"],
                ["04", "Versand & Nachweis", "Freigabe · E-Mail · Zustellstatus"],
              ].map(([number, title, details], index) => (
                <React.Fragment key={title}>
                  <div className={`architecture-layer layer-${index + 1}`}><span>{number}</span><strong>{title}</strong><small>{details}</small></div>
                  {index < 3 && <div className="layer-connector"><span/><span/><span/></div>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-shell faq-section" id="faq" aria-labelledby="faq-title">
          <div className="section-heading centered narrow">
            <p className="eyebrow">Häufige Fragen</p>
            <h2 id="faq-title">Gut zu wissen, bevor Sie starten.</h2>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <details key={faq.question} open={index === 0}>
                <summary>{faq.question}<span>+</span></summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <Contact />
      </main>

      <SiteFooter />
    </div>
  );
}

export default App;
